import React, { InputHTMLAttributes, forwardRef, useId } from 'react';
import type {
  FieldErrors,
  RegisterOptions,
  UseFormRegisterReturn,
  UseFormWatch,
} from 'react-hook-form';

import Description from '../description/Description';

interface InputProps {
  name: string;
  type?: InputHTMLAttributes<HTMLInputElement>['type'];
  disabled?: boolean;
  readOnly?: boolean;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  description?: string | React.ReactNode;
  maxLength?: number;
  styleWidthSize?: string;
}

interface TextareaWithHookFormProps extends InputProps {
  register?: (name: string, rules?: RegisterOptions) => UseFormRegisterReturn;
  errors?: FieldErrors;
  rules?: RegisterOptions;
  watch?: UseFormWatch<any>;
}

export default function withTextareaHookForm<
  T extends React.ComponentType<any>,
>(Component: T) {
  const WrappedComponent = forwardRef(
    (
      {
        name,
        type,
        register,
        errors,
        rules,
        onChange,
        onBlur,
        description,
        disabled,
        readOnly,
        maxLength,
        watch,
        styleWidthSize = 'auto',
        ...props
      }: InputProps & {
        register?: any;
        errors?: any;
        rules?: any;
        watch?: any;
      },
      ref: React.ForwardedRef<any>,
    ) => {
      const descriptionId = useId();
      const hasError = !!errors?.[name];
      const hasDescription = hasError || description || (watch && maxLength);
      const {
        'aria-describedby': ariaDescribedBy,
        'aria-invalid': ariaInvalid,
        ...restProps
      } = props as any;
      const describedBy = [ariaDescribedBy, hasDescription && descriptionId]
        .filter(Boolean)
        .join(' ');

      return (
        <div>
          <Component
            type={type}
            name={name}
            onChange={onChange}
            onBlur={onBlur}
            hasError={hasError}
            disabled={disabled}
            readOnly={readOnly}
            ref={ref}
            styleWidthSize={styleWidthSize}
            aria-invalid={ariaInvalid ?? (hasError || undefined)}
            aria-describedby={describedBy || undefined}
            {...(register && register(name, rules))}
            {...restProps}
          />
          <Description
            id={descriptionId}
            name={name}
            errors={errors}
            description={description}
            watch={watch}
            maxLength={maxLength}
            styleWidthSize={styleWidthSize}
          />
        </div>
      );
    },
  ) as React.ComponentType<
    React.ComponentPropsWithoutRef<T> & Omit<TextareaWithHookFormProps, 'ref'>
  >;

  return WrappedComponent;
}
